import { FC } from "react";
import { ChatMessage, LoadingState } from "../../types";
import { StatusUpdates } from "../statusUpdates";
import { InteractionPrompt } from "../interactionPrompt";
import { ChatMessageActions } from "../chatMessageActions";
import { Markdown, Stack } from "@altimateai/lego";
import { Artifacts } from "../artifacts";
import Citations from "../citations/Citations";
import Followup from "../followup/Followup";
import { useChatContext } from "../../ChatProvider";
import { TodoProgress } from "../todoProgress";
import { AttachmentList } from "../attachmentList";
import { Button } from "@altimateai/lego";
import { IconDeviceFloppy } from "@tabler/icons-react";
import { Message, MessageContent } from "@altimateai/lego";
import { cn } from "../../../../shared/utils";

interface MessageItemProps {
  message: ChatMessage;
  isLastMessage?: boolean;
  isFirstMessage?: boolean;
  handleSavePrompt?: (message: ChatMessage) => void;
  showSavePromptButton?: boolean;
}

export const MessageItem: FC<MessageItemProps> = ({
  message,
  isLastMessage,
  isFirstMessage,
  handleSavePrompt,
  showSavePromptButton,
}) => {
  const isAssistant = message.role === "assistant";
  const { currentSessionId, loadingState, urls, provider, classNames = {} } = useChatContext();

  return (
    <Message from={message.role} key={message.id}>
      <Stack direction="column" gap="none">
        {message.attachments && message.attachments.length > 0 && (
          <Stack gap="sm" justify={isAssistant ? "start" : "end"}>
            <div id={`message-${message.id}-attachments`}>
              <AttachmentList attachments={message.attachments} />
            </div>
          </Stack>
        )}
        <Stack justify="end">
          {isFirstMessage && showSavePromptButton && handleSavePrompt && (
            <Button
              variant={"ghost"}
              size={"icon"}
              onClick={() => handleSavePrompt(message)}
              className="al-text-muted-foreground !al-p-0 al-h-auto al-w-auto al-mt-2"
              aria-label="Save prompt"
              title="Add to Prompt Library"
              type="button"
            >
              <IconDeviceFloppy className={"!al-size-5"} />
            </Button>
          )}
          <MessageContent>
            <Stack gap="sm" direction="column" className="group-[.is-assistant]:al-w-full">
              <div
                id={`message-${message.id}`}
                className={cn(
                  `al-flex al-items-start al-gap-2`,
                  !isAssistant ? "al-justify-end" : "",
                  classNames.messageItem ?? ""
                )}
                role="article"
                aria-label={`${isAssistant ? "Assistant" : "User"} message`}
              >
                <Stack direction="row" className="group-[.is-assistant]:al-w-full">
                  <div className="al-max-w-full al-flex-1">
                    {message.role === "assistant" &&
                      message.actions &&
                      message.actions.length > 0 && (
                        <div id={`message-${message.id}-status`}>
                          <StatusUpdates
                            actions={message.actions}
                            isComplete={true}
                            heading={message.heading}
                          />
                        </div>
                      )}

                    {message.role === "assistant" && message.todos && message.todos.length > 0 && (
                      <div id={`message-${message.id}-todos`}>
                        <TodoProgress todos={message.todos} />
                      </div>
                    )}
                    {/* {message.role === "assistant" && message.confidence_str && (
                  <Alert className="al-my-3" variant="warning">
                    <Stack gap="xl" align="center">
                      <IconAlertSquareRounded className="al-min-h-6 al-min-w-6" />
                      <Typography variant="body" className="al-flex-1">
                        {message.confidence_str}
                      </Typography>
                    </Stack>
                  </Alert>
                )} */}
                    <div id={`message-${message.id}-content`}>
                      {isAssistant ? (
                        <Markdown
                          content={message.content || ""}
                          id={message.id}
                          provider={provider}
                        />
                      ) : (
                        // Pentest 2026-Q2 ("HTML Injection in Studio Chat"): user-typed
                        // content must never render as HTML/markdown. React escapes this
                        // string, so tags like <h1>/<a>/<script> show as literal text.
                        // The assistant branch keeps full Markdown (citations, headings,
                        // etc.) completely unchanged.
                        <div className="al-whitespace-pre-wrap al-break-words">
                          {message.content || ""}
                        </div>
                      )}
                    </div>

                    {message.artifacts && (
                      <div id={`message-${message.id}-artifacts`}>
                        <Artifacts artifacts={message.artifacts} />
                      </div>
                    )}

                    {message.interaction && (
                      <div className="al-mt-4" id={`message-${message.id}-interaction`}>
                        <InteractionPrompt interaction={message.interaction} />
                      </div>
                    )}

                    {message.citations && message.citations.length > 0 && (
                      <div className="al-mt-4" id={`message-${message.id}-citations`}>
                        <Citations
                          citations={message.citations}
                          frontEndUrl={urls?.frontendUrl || ""}
                        />
                      </div>
                    )}

                    {isAssistant && loadingState === LoadingState.LOADED && (
                      <div id={`message-${message.id}-actions`}>
                        <ChatMessageActions
                          messageId={message.id}
                          messageContent={message.content}
                          sessionId={currentSessionId || undefined}
                          isLastMessage={isLastMessage || false}
                        />
                      </div>
                    )}
                  </div>
                </Stack>
              </div>
              {isLastMessage && currentSessionId && loadingState === LoadingState.LOADED && (
                <Followup />
              )}
            </Stack>
          </MessageContent>
        </Stack>
      </Stack>
    </Message>
  );
};
